import { useTranslation } from 'react-i18next';
import { FC, useState } from 'react';
import { productCategoryTranslations, soldProductsTranslations } from '../../../translations';
import { Button, DropdownMenuItem, FilterDrawer, FilterType } from '../../../stories/molecules';
import { Icons } from '../../../stories/atoms';
import { SoldProductItem } from '../../../hooks';

export type SoldProductsMobileHeaderProps = {
  filteredItems: SoldProductItem[];
  departmentDropdownMenuItems: DropdownMenuItem[];
  indexDropdownMenuItems: DropdownMenuItem[];
  subIndexDropdownMenuItems: DropdownMenuItem[];
  filteredDepartmentNames: string[];
  filteredIndexes: string[];
  filteredSubIndexes: string[];
  updateFilteredItems: (items: string[], itemType: 'department' | 'index' | 'subIndex') => void;
  clearAllFilters: () => void;
};

export const SoldProductsMobileHeader: FC<SoldProductsMobileHeaderProps> = ({
  filteredItems,
  departmentDropdownMenuItems,
  indexDropdownMenuItems,
  subIndexDropdownMenuItems,
  filteredDepartmentNames,
  filteredIndexes,
  filteredSubIndexes,
  updateFilteredItems,
  clearAllFilters,
}) => {
  const { t } = useTranslation();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const handleFilteredItems = (items: string[], type: 'department' | 'index' | 'subIndex') => {
    updateFilteredItems(items, type);
  };

  return (
    <div data-testid="sold-products-mobile-header">
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onDrawerClose={() => setIsFilterDrawerOpen(false)}
        onClearAllFilters={clearAllFilters}
        totalResultEntries={filteredItems.length}
        totalResultEntriesText={t(
          productCategoryTranslations.filter.entriesText.key,
          productCategoryTranslations.filter.entriesText.defaultValue,
        )}
        filtersSpecs={[
          {
            key: 'department',
            type: FilterType.MultiSelectList,
            title: t(
              soldProductsTranslations.productDetailsView.department.key,
              soldProductsTranslations.productDetailsView.department.defaultValue,
            ),
            options: departmentDropdownMenuItems.map((item) => ({
              key: item.key,
              title: item.key,
              disabled: item.disabled,
            })),
            selectedOptionsKeys: filteredDepartmentNames,
            onSelect: (selectedItemKeys) => handleFilteredItems(selectedItemKeys, 'department'),
          },
          {
            key: 'index',
            type: FilterType.MultiSelectList,
            title: t(
              soldProductsTranslations.productDetailsView.index.key,
              soldProductsTranslations.productDetailsView.index.defaultValue,
            ),
            options: indexDropdownMenuItems.map((item) => ({
              key: item.key,
              title: item.key,
              disabled: item.disabled,
            })),
            selectedOptionsKeys: filteredIndexes,
            onSelect: (selectedItemKeys) => handleFilteredItems(selectedItemKeys, 'index'),
          },
          {
            key: 'subIndex',
            type: FilterType.MultiSelectList,
            title: t(
              soldProductsTranslations.productDetailsView.subIndex.key,
              soldProductsTranslations.productDetailsView.subIndex.defaultValue,
            ),
            options: subIndexDropdownMenuItems.map((item) => ({
              key: item.key,
              title: item.key,
              disabled: item.disabled,
            })),
            selectedOptionsKeys: filteredSubIndexes,
            onSelect: (selectedItemKeys) => handleFilteredItems(selectedItemKeys, 'subIndex'),
          },
        ]}
      />

      <Button
        data-testid="all-filters-button"
        label={t(
          productCategoryTranslations.filter.allFilters.key,
          productCategoryTranslations.filter.allFilters.defaultValue,
        )}
        buttonContentProps={{
          labelPosition: 'left',
          iconOptions: {
            icon: Icons.FILTER,
            size: 'small',
          },
          textOptions: {
            whiteSpace: 'nowrap',
          },
        }}
        aria-haspopup="true"
        aria-expanded={isFilterDrawerOpen ? 'true' : undefined}
        onClick={() => setIsFilterDrawerOpen(true)}
        variant="text"
      />
    </div>
  );
};
